/**
 * Desktop controls -> abstract intents.
 *  - Mouse position relative to the (centred) player sets movement direction,
 *    with an analog throttle that ramps up to the control radius.
 *  - Left mouse button = grow (one pulse per press).
 *  - Right mouse button = shrink (context menu suppressed).
 *  - Spacebar = shrink (backup binding for trackpads).
 */
import type { GameConfig } from '../config/config';
import { clamp } from '../core/math/vec2';
import { emptyFrame, type InputFrame, type InputSource } from './inputSource';

export class DesktopInput implements InputSource {
  private readonly canvas: HTMLCanvasElement;
  private readonly cfg: GameConfig;

  private pointerX = 0;
  private pointerY = 0;
  private growClicks = 0;
  private shrinkClicks = 0;
  private spaceDown = false;
  private active = false;

  constructor(canvas: HTMLCanvasElement, cfg: GameConfig) {
    this.canvas = canvas;
    this.cfg = cfg;
  }

  start(): void {
    if (this.active) return;
    this.active = true;
    // Centre the pointer initially so the player idles until the mouse moves.
    const rect = this.canvas.getBoundingClientRect();
    this.pointerX = rect.width / 2;
    this.pointerY = rect.height / 2;

    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  stop(): void {
    this.active = false;
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('contextmenu', this.onContextMenu);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
  }

  drain(): InputFrame {
    const rect = this.canvas.getBoundingClientRect();
    const dx = this.pointerX - rect.width / 2;
    const dy = this.pointerY - rect.height / 2;
    const dist = Math.hypot(dx, dy);

    const frame: InputFrame = emptyFrame();
    if (dist > this.cfg.input.deadzonePx) {
      const throttle = clamp(dist / this.cfg.input.controlRadiusPx, 0, 1);
      frame.moveDir = { x: (dx / dist) * throttle, y: (dy / dist) * throttle };
    }
    if (this.growClicks) frame.pulses.grow = this.growClicks;
    if (this.shrinkClicks) frame.pulses.shrink = this.shrinkClicks;

    this.growClicks = 0;
    this.shrinkClicks = 0;
    return frame;
  }

  private onMouseMove = (e: MouseEvent): void => {
    const rect = this.canvas.getBoundingClientRect();
    this.pointerX = e.clientX - rect.left;
    this.pointerY = e.clientY - rect.top;
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (e.button === 0) this.growClicks++;
    else if (e.button === 2) {
      this.shrinkClicks++;
      e.preventDefault();
    }
  };

  private onContextMenu = (e: Event): void => {
    e.preventDefault();
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.code === 'Space') {
      if (!this.spaceDown) this.shrinkClicks++; // ignore key-repeat
      this.spaceDown = true;
      e.preventDefault();
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (e.code === 'Space') this.spaceDown = false;
  };
}
